import { Button } from '@mantine/core';
import { IconPaperclip } from '@tabler/icons-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AttachmentCard } from './AttachmentCard.js';
import work from './PmsWork.module.css';
import type { PmsAttachment } from './types.js';

export interface OwnerAttachmentsProps {
  /** Ekin sahibi (bulgu, kasa hareketi). */
  ownerId: string;
  /** Sahibin ekleri; kaynak vermezse bölüm görünmez. */
  list: (ownerId: string) => Promise<PmsAttachment[]>;
  /** Gemide yükleme (JPG, PNG, PDF; fotoğraf küçültülür); ofiste ve kilitli kayıtta yok. */
  upload?: ((ownerId: string, file: File) => Promise<unknown>) | undefined;
  /** Ofiste açma adresi. */
  href?: ((attachmentId: string) => string) | undefined;
  /** Boş liste metni ve düğme etiketi. */
  emptyText: string;
  addLabel: string;
  /** Liste bölgesinin adı (ekran okuyucu). */
  label: string;
  onError: (error: unknown) => void;
}

/**
 * Bir kaydın dosya ekleri (ADR 0121; K15 altyapısı): liste, ofiste açma, gemide yükleme. Ek
 * değiştirilemez; yanlış ek gerekçeli iptalle geçmişte kalır [NR496 4.3.2a]. Dosya gemiden buluta
 * kendi kuyruğuyla gider; ofiste gelmemiş dosya "Uploading from vessel" ya da "File on vessel".
 */
export function OwnerAttachments({
  ownerId,
  list,
  upload,
  href,
  emptyText,
  addLabel,
  label,
  onError,
}: OwnerAttachmentsProps) {
  const [items, setItems] = useState<PmsAttachment[] | null>(null);
  const [busy, setBusy] = useState(false);

  // Çağıranın satır içi işlevleri her çizimde yenidir: liste yalnız sahip değişince okunur
  const latest = useRef({ list, onError });
  latest.current = { list, onError };
  // Liste okunamazsa ekranın bildirimine düşer (ör. yetkisiz); bölüm boş kalır
  const load = useCallback(async () => {
    try {
      setItems(await latest.current.list(ownerId));
    } catch (error) {
      latest.current.onError(error);
    }
  }, [ownerId]);
  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className={work.attachmentSection}>
      {items === null ? null : items.length === 0 ? (
        <p className={work.note}>{emptyText}</p>
      ) : (
        <ul className={work.attachmentList} aria-label={label}>
          {items.map((attachment) => (
            <AttachmentCard
              key={attachment.id}
              attachment={attachment}
              {...(href ? { href: href(attachment.id) } : {})}
            />
          ))}
        </ul>
      )}
      {upload ? (
        <div className={work.uploadRow}>
          <Button
            component="label"
            size="xs"
            variant="light"
            leftSection={<IconPaperclip size={14} />}
            loading={busy}
          >
            {addLabel}
            <input
              type="file"
              hidden
              accept="image/jpeg,image/png,application/pdf"
              onChange={(event) => {
                const file = event.currentTarget.files?.[0];
                event.currentTarget.value = '';
                if (!file) return;
                setBusy(true);
                void upload(ownerId, file)
                  .then(load)
                  .catch((error: unknown) => latest.current.onError(error))
                  .finally(() => setBusy(false));
              }}
            />
          </Button>
        </div>
      ) : null}
    </div>
  );
}
